import {request} from '@/helpers/apiClient';
import {REWARDS_CONFIG} from '@/utils/constants/apiConfig/rewardsConfig';
import {createRequestGate} from '@/helpers/requestGate';
import {buildQueryParams} from '@/utils/commons';

const digitalCodesListGate = createRequestGate({ttlMs: 10 * 1000});

export const invalidateDigitalCodesListCache = () => {
  digitalCodesListGate.clearCache?.();
};

const normaliseUsedFilter = (filters = []) => {
  return (filters || [])
    .filter((filter) => {
      // Drop used filter entirely when its value is empty/null/undefined (All)
      if (filter && filter.field === 'used') {
        return (
          filter.value !== '' &&
          filter.value !== null &&
          filter.value !== undefined
        );
      }
      return true;
    })
    .map((filter) => {
      if (!filter || filter.field !== 'used') return filter;

      const normalised = {...filter, type: 'boolean'};

      if (typeof filter.value === 'string') {
        const v = filter.value.toLowerCase();
        if (v === 'yes') {
          normalised.value = true;
        } else if (v === 'no') {
          normalised.value = false;
        } else if (v === 'true') {
          normalised.value = true;
        } else if (v === 'false') {
          normalised.value = false;
        } else {
          normalised.value = !!filter.value;
        }
      } else {
        normalised.value = !!filter.value;
      }

      return normalised;
    });
};

/**
 * Fetch digital codes list.
 *
 * @param {string} queryParams - Query string, e.g. "page=0&rewardId=4052&pageSize=100".
 * @param {Array} filters - Array of filter objects to send in the body.
 *
 * Example curl equivalent:
 *   curl --location '{{url}}api/v1/rewards/digital-codes/list?page=0&rewardId=4052&pageSize=100' \
 *   --header 'Content-Type: application/json' \
 *   --header 'Authorization: Bearer {{token}}' \
 *   --data '[{ "field": "expiryDate", "value": "2025-11-19, 2026-03-19", "type": "dateRange" }]'
 */
export const getDigitalCodesList = async (queryParams, filters) => {
  const normalisedFilters = normaliseUsedFilter(filters);

  const key = `${String(queryParams || '')}::${JSON.stringify(normalisedFilters || [])}`;
  return digitalCodesListGate({
    key,
    fetcher: async () =>
      request(REWARDS_CONFIG.DIGITAL_CODES_LIST(queryParams), {
        data: normalisedFilters
      })
  });
};

/**
 * Delete digital codes by IDs.
 */
export const deleteDigitalCodes = async ({
  rewardId,
  digitalCodes = []
} = {}) => {
  const queryParams = buildQueryParams({rewardId});

  const res = await request(REWARDS_CONFIG.DIGITAL_CODES_DELETE(queryParams), {
    data: {digitalCodes}
  });

  // After a delete, the list endpoint would otherwise return a cached response
  // (requestGate TTL is 30s). Clear the cache so the next list request fetches
  // fresh data.
  invalidateDigitalCodesListCache();

  return res;
};
